import { useState, useEffect } from 'react';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { RefreshCw, CheckCircle, XCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const ConnectionDiagnostics = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchDiagnostics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/connections/debug`);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch diagnostics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchDiagnostics();
    }
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-6 shadow-lg text-center">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Login Required</h2>
          <p className="text-gray-600">Please log in to view connection diagnostics.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 pb-20">
      <div className="max-w-lg mx-auto">
        <Link to="/profile" className="inline-flex items-center text-primary mb-4">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Profile
        </Link>

        <h1 className="text-2xl font-bold mb-6">Connection Diagnostics</h1>

        {loading ? (
          <div className="bg-white rounded-xl p-8 shadow-lg text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
            <p>Loading diagnostics...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <div className="flex items-center gap-3 text-red-600 mb-4">
              <XCircle className="w-6 h-6" />
              <span className="font-semibold">Error</span>
            </div>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={fetchDiagnostics} className="w-full">
              Try Again
            </Button>
          </div>
        ) : data ? (
          <div className="space-y-4">
            {/* User Info */}
            <div className="bg-white rounded-xl p-4 shadow-lg">
              <h2 className="font-bold text-lg mb-3">Your Account</h2>
              <p className="font-semibold">{data.name}</p>
              <p className="text-sm text-gray-500 break-all">ID: {data.user_id}</p>
            </div>

            {/* Field Status */}
            <div className="bg-white rounded-xl p-4 shadow-lg">
              <h2 className="font-bold text-lg mb-3">Database Field Status</h2>
              
              <div className="space-y-2">
                <FieldStatus 
                  label="pending_connections" 
                  exists={data.pending_connections_from_db !== undefined}
                  value={data.pending_connections_from_db}
                />
                <FieldStatus 
                  label="sent_connections" 
                  exists={data.sent_connections_from_db !== undefined}
                  value={data.sent_connections_from_db}
                />
                <FieldStatus 
                  label="contacts" 
                  exists={data.contacts_from_db !== undefined}
                  value={data.contacts_from_db}
                />
              </div>

              {(data.pending_connections_from_db === undefined || 
                data.sent_connections_from_db === undefined || 
                data.contacts_from_db === undefined) && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-amber-800 text-sm">
                    <strong>Missing fields detected!</strong><br />
                    Ask an admin to run "Initialize Connection Fields" from Admin Dashboard → Tools
                  </p>
                </div>
              )}
            </div>

            {/* Pending Requests */}
            <div className="bg-white rounded-xl p-4 shadow-lg">
              <h2 className="font-bold text-lg mb-2">Pending Requests</h2>
              <p className="text-gray-600 text-sm mb-3">People who want to connect with you:</p>
              <p className="text-3xl font-bold text-primary">
                {data.pending_connections_from_db?.length || 0}
              </p>
              {data.pending_connections_from_db?.length > 0 && (
                <div className="mt-3 space-y-1">
                  {data.pending_connections_from_db.map((id, i) => (
                    <p key={i} className="text-sm bg-gray-100 rounded px-2 py-1 break-all">
                      {id}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* Sent Requests */}
            <div className="bg-white rounded-xl p-4 shadow-lg">
              <h2 className="font-bold text-lg mb-2">Sent Requests</h2>
              <p className="text-gray-600 text-sm mb-3">Requests you've sent (waiting for response):</p>
              <p className="text-3xl font-bold text-amber-600">
                {data.sent_connections_from_db?.length || 0}
              </p>
              {data.sent_connections_from_db?.length > 0 && (
                <div className="mt-3 space-y-1">
                  {data.sent_connections_from_db.map((id, i) => (
                    <p key={i} className="text-sm bg-gray-100 rounded px-2 py-1 break-all">
                      {id}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* Connections */}
            <div className="bg-white rounded-xl p-4 shadow-lg">
              <h2 className="font-bold text-lg mb-2">Your Connections</h2>
              <p className="text-gray-600 text-sm mb-3">People you're connected with:</p>
              <p className="text-3xl font-bold text-green-600">
                {data.contacts_from_db?.length || 0}
              </p>
            </div>

            {/* Refresh Button */}
            <Button 
              onClick={fetchDiagnostics} 
              className="w-full py-6 text-lg"
              disabled={loading}
            >
              {loading ? (
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-5 h-5 mr-2" />
              )}
              Refresh
            </Button>

            <p className="text-center text-xs text-gray-400">
              Last updated: {new Date().toLocaleString()}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const FieldStatus = ({ label, exists, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
    <span className="text-sm font-medium">{label}</span>
    <div className="flex items-center gap-2">
      {exists ? (
        <>
          <CheckCircle className="w-5 h-5 text-green-500" />
          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
            EXISTS ({Array.isArray(value) ? value.length : '?'})
          </span>
        </>
      ) : (
        <>
          <XCircle className="w-5 h-5 text-red-500" />
          <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
            MISSING
          </span>
        </>
      )}
    </div>
  </div>
);

export default ConnectionDiagnostics;
